<script setup lang="ts">
import { ChevronDownIcon, ChevronUpIcon } from 'mdi-vue3'
import { computed, ref } from 'vue'

import { ABadge } from '../ABadge'
import AButton from '../AButton/AButton.vue'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../dropdown-menu'
import type { TableFilterOperator, TableFilterOptions, TableFilterValueOptions } from '../table/index.ts'
import { useGetFilterValueLabel } from '../table/index.ts'
import TableFilter from '../table/TableFilter.vue'

const properties = defineProps<{
  filters: TableFilterOptions[]
}>()

const activeFilters = defineModel<Record<string, TableFilterValueOptions>>({ required: true })
const openedId = ref<string | undefined>()

const getFilterValueLabel = useGetFilterValueLabel()

interface MappedFilter {
  filter: TableFilterOptions
  operator: TableFilterOperator | undefined
  values: string[]
}

const mappedFilters = computed((): MappedFilter[] => {
  return Object.entries(activeFilters.value)
    .map(([id, { operator, values }]) => {
      const filter = properties.filters.find((filter) => filter.id === id)
      if (!filter) {
        return
      }
      return { filter, operator, values }
    })
    .filter((filter): filter is Exclude<typeof filter, undefined> => !!filter)
})

function toggleFilter(filter: TableFilterOptions): void {
  setOpenFilter(filter, openedId.value !== filter.id)
}
function setOpenFilter(filter: TableFilterOptions, open: boolean): void {
  openedId.value = open ? filter.id : undefined
}

function onReset(filter: TableFilterOptions): void {
  delete activeFilters.value[filter.id]
}
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <DropdownMenu
      v-for="{ filter, operator, values } in mappedFilters"
      :key="filter.id"
      align="start"
      :open="openedId == filter.id"
      @update:open="(opened) => setOpenFilter(filter, opened)"
    >
      <DropdownMenuTrigger as-child>
        <AButton
          :icon="openedId !== filter.id ? ChevronDownIcon : ChevronUpIcon"
          icon-position="right"
          variant="outline"
          @click="toggleFilter(filter)"
        >
          {{ filter.label ?? '-' }}:
          <ABadge variant="filled">
            <span v-if="operator === 'greater'">&gt;</span>
            <span v-if="operator === 'greater_than_or_equal_to'">≥</span>
            <span v-if="operator === 'less_than_or_equal_to'">≤</span>
            <span v-if="operator === 'less'">&lt;</span>

            <span>{{ getFilterValueLabel(filter, values[0]) }}</span>

            <span v-if="filter?.operator === 'range' && values[1]">&hellip;</span>
            <span v-if="filter?.operator === 'range' && values[1]">
              {{ getFilterValueLabel(filter, values[1]) }}
            </span>
          </ABadge>

          <span v-if="values.length > 1 && operator !== 'range'" class="text-tag-foreground">+ {{ values.length - 1 }}</span>
        </AButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-[300px]">
        <TableFilter v-model:model-value="activeFilters[filter.id]" :filter="filter" @close="setOpenFilter(filter, false)" @reset="onReset(filter)" />
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
